import { z } from "zod";

// Base schemas for data validation
export const typeSchema = z.object({
  Name: z.string().min(1).max(200).transform(s => s.trim().replace(/\s+/g, ' ')),
  DataLabel: z.string().min(1).max(200),
  Version: z.number().int().min(0).default(0),
  XsdSchema: z.string().optional(),
  IsActive: z.boolean().optional(),
  ApplicationScope: z.string().optional().nullable(),
  ApplicationID: z.number().int().min(0).default(0),
  ID: z.number().int().min(0).default(0),
  CreatedDateTime: z.string().datetime().default(() => new Date().toISOString()),
  ExistingDataTypeLink: z.number().optional(),
  hasAttributes: z.boolean().optional(),
  UseAllAttributes: z.boolean().optional().default(false),
  AttributeIds: z.array(z.number()).optional(),
});

export const codeSchema = z.object({
  ID: z.number().int().min(0).default(0),
  DataTypeId: z.number().int().min(0).default(0),
  name: z.string()
    .min(1)
    .max(200)
    .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, {
      message: "Name cannot start with a number, contain spaces, or special characters (except underscores). Underscores and numbers are allowed after the first character."
    })
    .transform(s => s.trim().replace(/\s+/g, ' ')),
  type: z.union([z.string(), z.number()]).optional(),
  label: z.string().max(500),
  cm_values: z.string().optional(),
  cm_value_id: z.number().optional().nullable(),
  IsRequired: z.boolean().default(true),
  DropdownKind: z.number().optional().nullable(),
  Link: z.string().optional().nullable(),
  childAttribute: z.string().optional().nullable(),
  ParentAttributeCode: z.string().optional().nullable(),
  SortOrder: z.number().int().min(0).default(0),
  CreatedAt: z.string().datetime().default(() => new Date().toISOString()),
});

export const auditEntrySchema = z.object({
  id: z.string(),
  timestamp: z.string().datetime(),
  action: z.enum(["CREATE", "UPDATE", "DELETE", "IMPORT", "EXPORT"]),
  entityType: z.enum(["TYPE", "CODE"]),
  entityKey: z.string(),
  oldValue: z.record(z.any()).optional(),
  newValue: z.record(z.any()).optional(),
  user: z.string(),
  note: z.string().optional()
});

export const settingsSchema = z.object({
  canonicalizeCode: z.boolean().default(true),
  enforceUniqueNames: z.boolean().default(true),
  nlpProvider: z.enum(["LocalRules", "NLPS_API", "Proxy"]).default("LocalRules"),
  apiEndpoint: z.string().url().optional(),
  autoSave: z.boolean().default(true),
  theme: z.enum(["light", "dark", "system"]).default("light")
});

export const aiQuerySchema = z.object({
  id: z.string(),
  query: z.string(),
  intent: z.string().optional(),
  entities: z.record(z.any()).optional(),
  confidence: z.number().min(0).max(1).optional(),
  result: z.any().optional(),
  timestamp: z.string().datetime(),
  user: z.string(),
  saved: z.boolean().default(false),
  name: z.string().optional()
});

// Root storage schema
export const storageSchema = z.object({
  types: z.array(typeSchema).default([]),
  codes: z.array(codeSchema).default([]),
  audit: z.array(auditEntrySchema).default([]),
  settings: settingsSchema.default({}),
  ai: z.object({
    queries: z.array(aiQuerySchema).default([]),
    savedQueries: z.array(aiQuerySchema).default([])
  }).default({ queries: [], savedQueries: [] })
});

// Export types
export type Type = z.infer<typeof typeSchema>;
export type Code = z.infer<typeof codeSchema>;
export type AuditEntry = z.infer<typeof auditEntrySchema>;
export type Settings = z.infer<typeof settingsSchema>;
export type AIQuery = z.infer<typeof aiQuerySchema>;
export type StorageData = z.infer<typeof storageSchema>;

// Insert schemas
export const insertTypeSchema = typeSchema.omit({ ID: true });
// For inserts, code is auto-assigned (numeric per parent group). Allow omitting 'code'.
export const insertCodeSchema = codeSchema
export const insertAuditEntrySchema = auditEntrySchema.omit({ timestamp: true });

export type InsertType = z.infer<typeof insertTypeSchema>;
export type InsertCode = z.infer<typeof insertCodeSchema>;
export type InsertAuditEntry = z.infer<typeof insertAuditEntrySchema>;

export const typeEdgeSchema = z.object({
  fromType: z.string(),
  toType: z.string(),
  sourceType: z.string(),
  targetType: z.string(),
  relation: z.string().optional(),
});

export type TypeEdge = z.infer<typeof typeEdgeSchema>;

export const insertTypeEdgeSchema = typeEdgeSchema.omit({});
export type InsertTypeEdge = z.infer<typeof insertTypeEdgeSchema>;
